import React, { useState } from 'react'
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useSensitiveSession } from '../../contexts/SensitiveSessionContext'

/**
 * Password Modal for Sensitive Note Operations
 * 
 * Prompts for password when moving notes to/from sensitive folders.
 * Shows appropriate messaging based on the operation type.
 * VALIDATES PASSWORD before allowing encryption/decryption operations.
 */

const SensitiveNotePasswordModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  operationType = 'move', // 'encrypt' | 'decrypt' | 'move'
  targetFolderName = '',
  loading = false 
}) => {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isValidating, setIsValidating] = useState(false)

  const { user } = useAuth()
  const { isLocalMode, getSensitivePassword } = useLocalMode()
  const { sessionPassword } = useSensitiveSession()

  /**
   * Validate password before allowing the operation
   * For encrypt operations, no validation is needed - password is used directly for encryption
   * For decrypt operations, validate against stored session password
   */
  const validatePassword = async (passwordToValidate) => {
    try {
      // For encrypt operations (moving TO sensitive folder), no validation needed
      // The password will be used directly for encryption
      if (operationType === 'encrypt') {
        console.log('🔐 SensitiveNotePasswordModal: Encrypt operation - no password validation needed')
        return true
      }
      
      // For decrypt operations (moving FROM sensitive folder), validate against stored password
      const storedPassword = isLocalMode ? getSensitivePassword() : sessionPassword
      
      if (!storedPassword) {
        const errorMessage = isLocalMode 
          ? 'No sensitive password set. Please set up sensitive folder access first.'
          : 'No sensitive session found. Please authenticate to access sensitive folders first.'
        throw new Error(errorMessage)
      }
      
      if (passwordToValidate !== storedPassword) {
        throw new Error('Invalid password. Please try again.')
      }
      
      return true
    } catch (error) {
      console.error('Password validation failed:', error)
      throw error
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!password.trim()) {
      setError('Password is required')
      return
    }

    setIsValidating(true)
    setError('')

    try {
      // 🔒 SECURITY: Validate password BEFORE allowing encryption/decryption
      console.log('🔐 SensitiveNotePasswordModal: Validating password before proceeding...')
      await validatePassword(password)
      
      console.log('✅ SensitiveNotePasswordModal: Password validation successful')
      
      // Only proceed with operation if password is valid
      await onConfirm(password)
      handleClose()
      
    } catch (error) {
      console.error('❌ SensitiveNotePasswordModal: Password validation or operation failed:', error)
      setError(error.message || 'Invalid password or operation failed')
    } finally {
      setIsValidating(false)
    }
  }

  const handleClose = () => {
    setPassword('')
    setShowPassword(false)
    setError('')
    onClose()
  }

  const getTitle = () => {
    switch (operationType) {
      case 'encrypt':
        return 'Moving to Sensitive Folder'
      case 'decrypt':
        return 'Moving from Sensitive Folder'
      default:
        return 'Sensitive Folder Operation'
    }
  }

  const getDescription = () => {
    switch (operationType) {
      case 'encrypt':
        return `This note will be encrypted and moved to the "${targetFolderName}" folder. Enter an encryption password to secure this note.`
      case 'decrypt':
        return `This note will be decrypted and moved from the "${targetFolderName}" folder. Enter your password to continue.`
      default:
        return 'Enter your password to perform this sensitive folder operation.'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <Lock className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {getTitle()}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Password required
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Description */}
          <div className="mb-6">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {getDescription()}
            </p>
          </div>

          {/* Security Notice */}
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                  Security Notice
                </p>
                <ul className="space-y-1 text-amber-700 dark:text-amber-300">
                  {operationType === 'encrypt' ? (
                    <>
                      <li>• Enter an encryption password to secure this note</li>
                      <li>• This password will be used to encrypt the note content</li>
                      <li>• You'll need this same password to decrypt the note later</li>
                      <li>• Choose a strong password and remember it</li>
                    </>
                  ) : (
                    <>
                      <li>• This operation requires your account password</li>
                      <li>• Password will be validated before proceeding</li>
                      <li>• Note content will be decrypted</li>
                      <li>• Password is not stored and is used only for this operation</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Password Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {operationType === 'encrypt' ? 'Encryption Password' : 'Your Password'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError('')
                }}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-red-500 focus:border-transparent
                         pr-12"
                placeholder="Enter your password"
                required
                disabled={loading || isValidating}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                disabled={loading || isValidating}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading || isValidating}
              className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 
                       hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || isValidating || !password.trim()}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2"
            >
              {loading || isValidating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isValidating ? 'Validating...' : 'Processing...'}
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Confirm
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SensitiveNotePasswordModal 